import React, {useEffect, useState} from 'react';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Alert from "@mui/material/Alert";
import FormControl from '@mui/material/FormControl';
import {useAuth} from "../providers/AuthProvider";
import {useNavigate, useLocation} from "react-router";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import {useStatus} from "../providers/MsgStatusProvider";
import Link from "@mui/material/Link";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import {IconButton, InputAdornment} from "@mui/material";
import {Visibility, VisibilityOff, Warning, Lock} from "@mui/icons-material";
import {useAnalytics} from "../context/AnalyticsContext";
import {AnalyticsEvents} from "../services/analytics/fathom";
import {TrackedButton} from "../components/TrackedButton";
import {ButtonEvents} from "../services/analytics/events";
import { validateFieldRealTime } from '../utils/formValidation';


function LoginForm() {
    // const classes = useStyles();
    const auth = useAuth();
    const { trackEvent } = useAnalytics();
    const location = useLocation();

    const {statusType, setStatusMessage, setIsAlertOpen, setStatusType} = useStatus();
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [isPasswordVisible, setIsPasswordVisible] = useState(false)
    const [rememberMe, setRememberMe] = useState(false);
    const navigate = useNavigate();
    const [errors, setErrors] = useState({});
    const [throttleRetry, setThrottleRetry] = useState(null);

    const handleSubmit = async (event) => {
        event.preventDefault();
        let timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        // Validate before submit
        const emailError = validateFieldRealTime('email', username);
        const passwordError = validateFieldRealTime('password', password);
        setErrors({ username: emailError, password: passwordError });
        if (emailError || passwordError || throttleRetry) return;

        // Save or remove email from localStorage based on remember me checkbox
        if (rememberMe) {
            localStorage.setItem('rememberedEmail', username);
        } else {
            localStorage.removeItem('rememberedEmail');
        }
        try {
            await auth.login(username, password, timezone);
            trackEvent(AnalyticsEvents.LOGIN_SUCCESS);
        } catch (error) {
            trackEvent(AnalyticsEvents.LOGIN_FAILURE);
        }
    };

    useEffect(() => {
        if (auth.user.length > 0) {
            navigate('/dashboard', {replace: true});
        }
    }, [auth.user]);

    useEffect(() => {
        const savedEmail = localStorage.getItem('rememberedEmail');
        if (savedEmail) {
            setUsername(savedEmail);
            setRememberMe(true);
        }
    }, []);

    // Handle redirect messages from waitlist status page
    useEffect(() => {
        if (location.state?.message) {
            setStatusMessage(location.state.message);
            setStatusType('info');
            setIsAlertOpen(true);
            
            // Pre-fill email if provided
            if (location.state?.email) {
                setUsername(location.state.email);
            }
            
            // Clear the location state to prevent message from persisting
            navigate(location.pathname, { replace: true, state: {} });
        }
    }, [location.state, setStatusMessage, setStatusType, setIsAlertOpen, navigate]);

    useEffect(() => {
        if (auth.errorMessage?.error) {
            setStatusMessage(auth.errorMessage.error)
            setStatusType('error');
            setIsAlertOpen(true)
            
            // Handle throttling error with retry_after
            if (auth.errorMessage.status === 429 && auth.errorMessage.retry_after) {
                setThrottleRetry(auth.errorMessage.retry_after);
                let interval = setInterval(() => {
                    setThrottleRetry((prev) => {
                        if (prev > 1) return prev - 1;
                        clearInterval(interval);
                        return null;
                    });
                }, 1000);
                return () => clearInterval(interval);
            } else {
                setThrottleRetry(null);
            }
        } else {
            setStatusMessage('');
            setIsAlertOpen(false);
            setThrottleRetry(null);
        }
    }, [auth.errorMessage]);


    function handleRememberMeChange(event) {
        setRememberMe(event.target.checked);
        if (!event.target.checked) {
            localStorage.removeItem('rememberedEmail');
        }
    }

    const handleMouseDownPassword = (event) => {
        event.preventDefault();
    };

    function handleHideShowPassword() {
        setIsPasswordVisible(!isPasswordVisible);
    }

    const handleEmailChange = (event) => {
        setUsername(event.target.value);
        const error = validateFieldRealTime('email', event.target.value);
        setErrors((prev) => ({ ...prev, username: error }));
    };
    const handlePasswordChange = (event) => {
        setPassword(event.target.value);
        const error = validateFieldRealTime('password', event.target.value);
        setErrors((prev) => ({ ...prev, password: error }));
    };

    return (
        <Grid contaner>
            <Grid item p={3} direction="column" spacing={2} justifyItems="center">
                {/* Left side */}
                <div>
                    <FormControl variant="standard">
                        {auth.isAuthenticated ? (
                            <Typography variant="h3" align="center">
                                You are already logged in
                            </Typography>
                        ) : (<>
                                <Typography variant="h1" align="center">
                                    Login
                                </Typography>
                                {Object.values(errors).some(Boolean) && (
                                    <Alert severity="error" sx={{ mb: 2 }}>
                                        {errors.username || errors.password}
                                    </Alert>
                                )}
                                
                                {/* Display backend errors (throttling, invalid credentials, etc.) */}
                                {auth.errorMessage?.error && (
                                    <Alert 
                                        severity={auth.errorMessage.status === 429 ? "warning" : "error"} 
                                        sx={{ mb: 2 }}
                                        icon={auth.errorMessage.status === 429 ? <Lock /> : undefined}
                                    >
                                        {auth.errorMessage.error}
                                        {throttleRetry && (
                                            <div style={{ 
                                                marginTop: '8px', 
                                                fontWeight: 'bold',
                                                fontSize: '0.9em',
                                                color: '#ed6c02'
                                            }}>
                                                🔒 Please wait {throttleRetry} seconds before trying again.
                                            </div>
                                        )}
                                    </Alert>
                                )}
                                
                                {/* Legacy error handling for backwards compatibility */}
                                {auth.errorMessage?.length > 0 && !auth.errorMessage?.error &&
                                    auth.errorMessage.map((error, index) => (
                                        <Alert key={index} severity={statusType} sx={{ mb: 2 }}>
                                            {error}
                                        </Alert>
                                    ))}

                                <form onSubmit={handleSubmit}>
                                    <TextField
                                        required
                                        variant="outlined"
                                        id="username"
                                        label="Email"
                                        // className={classes.textField}
                                        type="text"
                                        value={username}
                                        onChange={handleEmailChange}
                                        margin="normal"
                                        fullWidth
                                        error={!!errors.username}
                                        helperText={errors.username}
                                    />
                                    <TextField
                                        required
                                        variant="outlined"
                                        id="password"
                                        label="Password"
                                        type={isPasswordVisible ? 'text' : 'password'}
                                        value={password}
                                        onChange={handlePasswordChange}
                                        margin="normal"
                                        fullWidth
                                        error={!!errors.password}
                                        helperText={errors.password}
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <IconButton
                                                        aria-label="toggle password visibility"
                                                        onClick={handleHideShowPassword}
                                                        onMouseDown={handleMouseDownPassword}
                                                        edge="end"
                                                    >
                                                        {isPasswordVisible ? <VisibilityOff/> : <Visibility/>}
                                                    </IconButton>
                                                </InputAdornment>
                                            )
                                        }}
                                    />
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={rememberMe}
                                                onChange={handleRememberMeChange}
                                                name="rememberMe"
                                                color="primary"
                                            />
                                        }
                                        label="Remember me"
                                    />
                                    <TrackedButton
                                        variant="contained"
                                        trackingData={ButtonEvents.LOGIN_BUTTON_CLICK}
                                        color="primary"
                                        fullWidth={true}
                                        type="submit"
                                        disabled={Object.values(errors).some(Boolean) || throttleRetry}
                                    >
                                        {throttleRetry 
                                            ? `Account locked - Try again in ${throttleRetry}s` 
                                            : 'Log in'
                                        }
                                    </TrackedButton>
                                    <Box display="flex" flexDirection="column" mt={2} gap={2}>
                                        <Link href="/forgot-password" variant="body2">
                                            Forgot password?
                                        </Link>
                                        <Link href="/signup" variant="body2">
                                            Don't have an account? Sign Up
                                        </Link>

                                    </Box>
                                </form>
                            </>
                        )}
                    </FormControl>
                </div>
            </Grid>
        </Grid>
    );
}

export default LoginForm;
